import { cn } from '../../lib/cn';

// THE 1–5 score family — single source (F9/U8). Every score-colored surface in
// the app derives from these three sets; nothing re-declares score hexes.
//
// Validated 2026-07-08 with the dataviz palette validator (light surface):
// lightness band + chroma floor PASS; worst adjacent CVD ΔE 10.5 (legal with
// the text/position encoding every score mark carries). The "good" pole is
// TEAL, not green, deliberately: red↔green collapses entirely under
// red-green colorblindness (a deutan reader could not tell a 1 from a 5),
// while red↔teal stays separable — and the warm arm (1→2→3) is spaced by
// LIGHTNESS, the channel CVD preserves.
//
// Which set to use where (dataviz mark rules):
//   SCORE_MARK — small marks: dots, thin bars, stacked segments, swatches.
//   SCORE_TINT — large fills and chips (a saturated block reads shouty):
//                heatmap cells, badges. Pair with SCORE_FG for text on it.
//   SCORE_FG   — ink for text sitting ON the matching tint.
// Hex by design: score colors are semantic DATA colors shared with chart
// fills, not theme-reactive UI tokens.
type Band = 1 | 2 | 3 | 4 | 5;

export const SCORE_MARK: Record<Band, string> = {
  1: '#b32d2d',
  2: '#d9752f',
  3: '#d1ac47',
  4: '#17a189',
  5: '#0b8067',
};

// Tints = marks mixed 72% toward white (visual weight of the pre-family
// pastels, so large fills read present, not washed out); FG = marks mixed
// 45% toward ink. Regenerate together if SCORE_MARK ever changes.
export const SCORE_TINT: Record<Band, string> = {
  1: '#eac4c4',
  2: '#f4d8c5',
  3: '#f2e8cb',
  4: '#bee5de',
  5: '#bbdbd4',
};

export const SCORE_FG: Record<Band, string> = {
  1: '#6b2423',
  2: '#804b24',
  3: '#7c6931',
  4: '#166355',
  5: '#0f5143',
};

// Ink for a label sitting ON a SCORE_MARK fill: the sand mid-step is light
// (needs ink), the other four are dark (need white).
export function scoreMarkInk(band: Band): string {
  return band === 3 ? 'rgba(24,22,12,0.78)' : 'rgba(255,255,255,0.92)';
}

export function scoreBand(score: number): Band {
  return Math.max(1, Math.min(5, Math.round(score))) as Band;
}

// The app-wide 1–5 score chip ("4/5" on its band tint). Missing/zero scores
// render as a dash — a score of 0 doesn't exist in the rubric, so showing
// "0/5" on the red band (as one of the old copies did) claimed a judgment
// that was never made.
//
// variant="summary" is the chip-shaped echo of the disc's summary grammar
// (tint body + solid cap): a roll-up — a skill's median of its specific
// skills — must never look identical to a mark a teacher gave. The cap is an
// inset bottom edge in the band's mark color.
export function ScoreChip({
  score,
  className,
  variant = 'grade',
  title,
}: {
  score: number | string | null | undefined;
  className?: string;
  variant?: 'grade' | 'summary';
  title?: string;
}) {
  const v = Number(score);
  if (!v) return <span className="text-muted-foreground">—</span>;
  const band = scoreBand(v);
  return (
    <span
      className={cn(
        'inline-block shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-bold tabular-nums',
        className
      )}
      title={title}
      style={{
        background: SCORE_TINT[band],
        color: SCORE_FG[band],
        ...(variant === 'summary' ? { boxShadow: `inset 0 -2px 0 ${SCORE_MARK[band]}` } : {}),
      }}
    >
      {Math.round(v)}/5
    </span>
  );
}